import { EventIcon } from '../../icons';
import { cn } from '../../lib/utils';
import { IconCount } from '../IconCount';

export interface EventCountStripProps {
  view: number;
  download: number;
  share: number;
  delete: number;
  /**
   * When provided, replaces the `view` cell with `read` (BookOpen glyph). The
   * value rendered in cell 1 becomes `read` and the icon flips. Back-compat
   * default: cell 1 stays `view`.
   */
  read?: number;
  /**
   * When provided, replaces the `share` cell with `write` (Pencil glyph). Cell
   * 3 becomes `write`. Back-compat default: cell 3 stays `share`.
   */
  write?: number;
  /**
   * Optional zero-tinting per Figma `5:23313`. When `true`, any cell whose
   * displayed value is `0` renders the icon in `text-grey-200` (`#D9D9D9`).
   * The number stays at IconCount's default tint of `text-dawn-black` (icon-
   * only dim — see TDEV-168 design.md Decision Log #2). Default: false.
   */
  dimZeroes?: boolean;
  /**
   * Subgrid mode for HTML-table-like column-width sharing across rows. When
   * the strip is nested inside a parent grid that exposes 4 sub-tracks for
   * the events sub-cells, set `subgrid={true}`. The strip becomes
   * `grid-template-columns: subgrid` spanning 4 parent columns so its
   * IconCount cells inherit the parent's auto-sized column tracks — every
   * sibling strip in the same parent grid then aligns icons to the same
   * cell-start positions and each sub-column sizes to the widest value
   * across rows. Default: `false` (self-contained 4×48px grid per Figma
   * `5:23356`, suitable for standalone use).
   */
  subgrid?: boolean;
  className?: string;
}

/**
 * `EventCountStrip` — 4-up cluster of `IconCount` cells (view / download /
 * share / delete) per Figma `289:48641`. Composes Phase 7 `IconCount` +
 * Phase 2 `EventIcon`. Default 14-px icon slot to match donut-center and
 * event-row densities.
 *
 * Layout is a 4-column equal-width grid (Figma `5:23357`: 4 × ~48px cells
 * with a 12px gap), so every icon sits at the same x-offset across rows
 * regardless of how wide the numeric label in the previous cell is. With a
 * pure `inline-flex` row the cell widths grow with the digit count and
 * subsequent icons shift right — the grid pins icon start positions.
 *
 * **Top Data Accessed extension (TDEV-168):** when `read`/`write` are passed,
 * cells 1 and 3 swap glyphs + values — `read` (BookOpen) replaces `view` (Eye);
 * `write` (Pencil) replaces `share` (Share2). `view`/`share` remain required
 * for back-compat; passing both is allowed and the optional values win.
 */
export function EventCountStrip({
  view,
  download,
  share,
  delete: del,
  read,
  write,
  dimZeroes = false,
  subgrid = false,
  className,
}: EventCountStripProps) {
  const cell1Count = read ?? view;
  const cell1IconName = read !== undefined ? 'read' : 'view';
  const cell3Count = write ?? share;
  const cell3IconName = write !== undefined ? 'write' : 'share';

  const dim = (value: number) => (dimZeroes && value === 0 ? 'text-grey-200' : undefined);

  return (
    <div
      className={cn(
        subgrid
          ? 'col-span-4 grid grid-cols-subgrid items-center gap-3'
          : 'grid grid-cols-[48px_48px_48px_48px] items-center gap-3',
        className
      )}
      data-testid="event-count-strip"
    >
      <IconCount
        icon={<EventIcon name={cell1IconName} className={dim(cell1Count)} />}
        count={cell1Count}
        size={14}
      />
      <IconCount
        icon={<EventIcon name="download" className={dim(download)} />}
        count={download}
        size={14}
      />
      <IconCount
        icon={<EventIcon name={cell3IconName} className={dim(cell3Count)} />}
        count={cell3Count}
        size={14}
      />
      <IconCount icon={<EventIcon name="delete" className={dim(del)} />} count={del} size={14} />
    </div>
  );
}

EventCountStrip.displayName = 'EventCountStrip';
